import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { getUserProfile, saveUserProfile, uploadUserDocument, deleteUserDocument } from '../api/users';
import { autocompleteAddress, getPlaceDetails } from '../api/address';
import { Avatar } from '../components/Avatar';
import { LoadingSpinner } from "../components/LoadingSpinner";
import { API_URL } from '../config/api';
import { SkillsInput } from '../components/SkillsInput';
import { normalizeAndValidatePhone, formatPhoneForDisplay } from '../utils/phoneValidation';
import { useProjectsStore, useProjectsPage } from '../state/projects';
import { useCurrentUserStore } from '../state/currentUser';

// Address suggestion interface
interface AddressSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

// Document row configuration
interface DocumentConfig {
  type: string;
  label: string;
  accept: string;
  inputId: string;
  documentUrl?: string;
}

// US States/Regions
const US_REGIONS = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming'
];

// --- Reusable DocumentRow component ---
const DocumentRow: React.FC<{
  config: DocumentConfig;
  isUploading: boolean;
  isViewing: boolean;
  onView: (url?: string) => void;
  onUpload: (type: string, file: File) => void;
  onDelete: (type: string) => void;
}> = ({ config, isUploading, isViewing, onView, onUpload, onDelete }) => (
  <div className="flex items-center justify-between p-3 border border-[#eaeaea] rounded-lg">
    <div>
      <p className="font-medium">{config.label}</p>
      <p className="text-sm text-[#888]">
        {config.documentUrl ? 'Uploaded' : 'Not uploaded'}
      </p>
    </div>
    <div className="flex space-x-2">
      {config.documentUrl && (
        <button
          onClick={() => onView(config.documentUrl)}
          disabled={isViewing}
          className={`px-3 py-1 text-white text-sm rounded flex items-center space-x-1 ${
            isViewing ? 'bg-blue-400 cursor-wait' : 'bg-[#555] hover:bg-[#171717]'
          }`}
        >
          {isViewing && (
            <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
          )}
          <span>{isViewing ? 'Opening...' : 'View'}</span>
        </button>
      )}
      <input
        type="file"
        accept={config.accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(config.type, file);
        }}
        className="hidden"
        id={config.inputId}
      />
      <label
        htmlFor={config.inputId}
        className="px-3 py-1 bg-[#171717] text-white text-sm rounded hover:bg-[#333] cursor-pointer"
      >
        {isUploading ? 'Uploading...' : 'Upload'}
      </label>
      {config.documentUrl && (
        <button
          onClick={() => onDelete(config.type)}
          className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
        >
          Delete
        </button>
      )}
    </div>
  </div>
);

// --- Profile Completeness Bar ---
const ProfileCompleteness: React.FC<{ profile: UserProfile }> = ({ profile }) => {
  const fields = [
    { label: 'Name', filled: !!(profile.first_name && profile.last_name) },
    { label: 'Email', filled: !!profile.email },
    { label: 'Phone', filled: !!profile.phone },
    { label: 'Position', filled: !!profile.position },
    { label: 'Location', filled: !!(profile.city && profile.region) },
    { label: 'Address', filled: !!profile.google_address_data?.formatted_address },
    { label: 'Skills', filled: !!(profile.skills && profile.skills.length > 0) },
    { label: 'Photo', filled: !!profile.photo_url },
  ];

  const filledCount = fields.filter(f => f.filled).length;
  const percentage = Math.round((filledCount / fields.length) * 100);

  if (percentage === 100) return null;

  return (
    <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-[#eaeaea] p-4 mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-[#444]">Profile completeness</span>
        <span className="text-sm font-semibold text-[#171717]">{percentage}%</span>
      </div>
      <div className="w-full bg-[#eaeaea] rounded-full h-2 mb-3">
        <div
          className="h-2 rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${percentage}%`,
            backgroundColor: percentage >= 80 ? '#22c55e' : percentage >= 50 ? '#eab308' : '#ef4444'
          }}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {fields.filter(f => !f.filled).map(f => (
          <span key={f.label} className="px-2 py-0.5 bg-[#f5f5f5] text-[#888] text-xs rounded-full">
            {f.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const Profile: React.FC = () => {
  const { keycloak } = useKeycloak();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [formData, setFormData] = useState<UserProfileFormData>({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    rate: 0,
    region: '',
    city: '',
    range_miles: 50,
    skills: [],
    position: '',
    photo_url: '',
    google_address_data: undefined
  });
  const [uploadingDocuments, setUploadingDocuments] = useState<{[key: string]: boolean}>({});
  const [viewingDocuments, setViewingDocuments] = useState<{[key: string]: boolean}>({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Address autocomplete state
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const addressSuggestionsRef = useRef<HTMLDivElement>(null);

  // Track if initial profile load has happened (prevents reload on token refresh)
  const hasLoadedProfile = useRef(false);

  // Track unsaved changes for beforeunload warning
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Get current user ID from Keycloak
  const currentUserId = keycloak?.tokenParsed?.sub || '';

  // Get projects from global store (loaded by sidebar)
  const allProjects = useProjectsStore((state) => state.projects);
  const { loadProjectsPage } = useProjectsPage();

  // Load projects into store if not already loaded (e.g. direct navigation to /profile)
  useEffect(() => {
    if (allProjects.length === 0 && keycloak?.authenticated) {
      loadProjectsPage(0);
    }
  }, [keycloak?.authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive user's projects: managed (owner) + member
  const userProjects = useMemo(() => {
    if (!currentUserId || !allProjects.length) return [];

    return allProjects.map((project) => ({
      id: project.id,
      name: project.name,
      description: project.description,
      status: project.status,
      role: project.owner_id === currentUserId ? 'Manager' : 'Member',
    }));
  }, [allProjects, currentUserId]);

  // Warn on page leave if editing with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isEditing && hasUnsavedChanges) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEditing, hasUnsavedChanges]);

  useEffect(() => {
    // Skip if already loaded to prevent overwriting saved data on token refresh
    if (hasLoadedProfile.current) return;

    if (keycloak?.authenticated && keycloak?.token && currentUserId) {
      hasLoadedProfile.current = true;
      loadUserProfile();
    } else {
      setIsNewProfile(true);

      const fallbackProfile: UserProfile = {
        id: currentUserId || 'unknown',
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        rate: 0,
        region: '',
        city: '',
        range_miles: 50,
        skills: [],
        position: '',
        google_address_data: undefined,
        projects: [],
        managers: [],
      };

      setProfile(fallbackProfile);
      populateFormData(fallbackProfile);
      setIsLoading(false);
    }
  }, [keycloak?.token, currentUserId]);

  // Address autocomplete with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (addressQuery.length >= 3 && keycloak?.token) {
        setIsLoadingAddress(true);
        try {
          const result = await autocompleteAddress(keycloak.token, addressQuery);
          setAddressSuggestions(result.predictions || []);
          setShowAddressSuggestions(true);
        } catch {
          setAddressSuggestions([]);
        } finally {
          setIsLoadingAddress(false);
        }
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [addressQuery, keycloak?.token]);

  // Handle click outside address suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressSuggestionsRef.current && !addressSuggestionsRef.current.contains(event.target as Node) &&
          addressInputRef.current && !addressInputRef.current.contains(event.target as Node)) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to populate form data from profile
  const populateFormData = (profileData: UserProfile) => {
    setFormData({
      first_name: profileData.first_name || '',
      last_name: profileData.last_name || '',
      phone: profileData.phone || '',
      email: profileData.email || '',
      rate: profileData.rate ?? 0,
      region: profileData.region || '',
      city: profileData.city || '',
      range_miles: profileData.range_miles ?? 50,
      skills: profileData.skills || [],
      position: profileData.position || '',
      photo_url: profileData.photo_url || '',
      google_address_data: profileData.google_address_data
    });
  };

  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      const profileData = await getUserProfile(keycloak?.token || '', currentUserId);

      // Parse skills if it's a string (legacy support)
      if (profileData.skills && typeof profileData.skills === 'string') {
        profileData.skills = (profileData.skills as string)
          .split(/[;,]/)
          .map((skill: string) => skill.trim())
          .filter((skill: string) => skill.length > 0);
      }

      setProfile(profileData);
      populateFormData(profileData);
      setAddressQuery(profileData.google_address_data?.formatted_address || '');
    } catch (error) {
      // Handle 422 errors gracefully - user profile doesn't exist yet
      if (error instanceof Error && error.message.includes('422')) {
        setIsNewProfile(true);

        const defaultProfile: UserProfile = {
          id: keycloak?.tokenParsed?.sub || '',
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          rate: 0,
          region: '',
          city: '',
          range_miles: 50,
          skills: [],
          position: '',
          photo_url: '',
          google_address_data: undefined,
          projects: [],
          managers: []
        };

        setProfile(defaultProfile);
        populateFormData(defaultProfile);
        toast.info('Please complete your profile setup');
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setHasUnsavedChanges(true);

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    // Real-time phone validation
    if (name === 'phone') {
      setPhoneError('');
      if (value) {
        const phoneValidation = normalizeAndValidatePhone(value);
        if (!phoneValidation.isValid) {
          setPhoneError(phoneValidation.error || 'Invalid phone number');
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: name === 'rate' || name === 'range_miles' ? Number(value) : value
    }));
  };

  // Handle address selection
  const handleAddressSelect = async (suggestion: AddressSuggestion) => {
    if (!keycloak?.token) return;

    setIsLoadingAddress(true);
    try {
      const placeDetails = await getPlaceDetails(keycloak.token, suggestion.place_id);

      let city = '';
      let region = '';

      if (placeDetails.address_components) {
        const cityComponent = placeDetails.address_components.find((c: any) =>
          c.types.includes('locality') || c.types.includes('administrative_area_level_2')
        );
        const stateComponent = placeDetails.address_components.find((c: any) =>
          c.types.includes('administrative_area_level_1')
        );

        if (cityComponent) city = cityComponent.long_name;
        if (stateComponent) region = stateComponent.long_name;
      }

      setFormData(prev => ({
        ...prev,
        google_address_data: placeDetails,
        city: city || prev.city,
        region: region || prev.region
      }));
      setAddressQuery(placeDetails.formatted_address);
      setShowAddressSuggestions(false);
      setHasUnsavedChanges(true);
    } catch {
      toast.error('Failed to load address details');
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const clearAddress = () => {
    setFormData(prev => ({ ...prev, google_address_data: undefined }));
    setAddressQuery('');
    setShowAddressSuggestions(false);
    setHasUnsavedChanges(true);
  };

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);

      // Validate mandatory fields
      const errors: Record<string, string> = {};
      if (!formData.first_name.trim()) errors.first_name = 'First name is required';
      if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
      if (!formData.email.trim()) errors.email = 'Email is required';
      if (!formData.phone.trim()) errors.phone = 'Phone is required';

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        toast.error('Please fill in all required fields');
        setIsSaving(false);
        return;
      }
      setFieldErrors({});

      // Validate and normalize phone before saving
      let normalizedPhone = formData.phone;
      if (formData.phone) {
        const phoneValidation = normalizeAndValidatePhone(formData.phone);
        if (!phoneValidation.isValid) {
          setPhoneError(phoneValidation.error || 'Invalid phone number');
          toast.error(phoneValidation.error || 'Invalid phone number');
          setIsSaving(false);
          return;
        }
        normalizedPhone = phoneValidation.normalizedPhone;
      }

      // Backend expects skills as array of strings
      let skillsValue: string[] | null = null;
      if (Array.isArray(formData.skills) && formData.skills.length > 0) {
        skillsValue = formData.skills;
      }

      const profileDataToSave = {
        ...formData,
        phone: normalizedPhone,
        skills: skillsValue
      };

      const updatedProfile = await saveUserProfile(
        keycloak?.token || '',
        currentUserId,
        profileDataToSave,
        isNewProfile
      );

      setProfile(updatedProfile);
      useCurrentUserStore.getState().setProfile(updatedProfile);
      setIsEditing(false);
      setIsNewProfile(false);
      setHasUnsavedChanges(false);
      toast.success(isNewProfile ? 'Profile created successfully!' : 'Profile updated successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      if (errorMessage.includes('422')) {
        toast.error('Invalid profile data. Please check all required fields.');
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        toast.error('You do not have permission to update this profile.');
      } else if (errorMessage.includes('500')) {
        toast.error('Server error occurred. Please try again in a few moments.');
      } else {
        toast.error(`Failed to save profile: ${errorMessage}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (profile) {
      populateFormData(profile);
      setAddressQuery(profile.google_address_data?.formatted_address || '');
    }
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  const handleDocumentUpload = async (documentType: string, file: File) => {
    try {
      setUploadingDocuments(prev => ({ ...prev, [documentType]: true }));
      await uploadUserDocument(keycloak?.token || '', currentUserId, documentType, file);
      toast.success(`${documentType.replace('_', ' ')} uploaded successfully`);
      loadUserProfile();
    } catch {
      toast.error(`Failed to upload ${documentType.replace('_', ' ')}`);
    } finally {
      setUploadingDocuments(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const handleDocumentDelete = async (documentType: string) => {
    if (window.confirm(`Are you sure you want to delete this ${documentType.replace('_', ' ')}?`)) {
      try {
        await deleteUserDocument(keycloak?.token || '', currentUserId, documentType);
        toast.success(`${documentType.replace('_', ' ')} deleted successfully`);
        loadUserProfile();
      } catch {
        toast.error(`Failed to delete ${documentType.replace('_', ' ')}`);
      }
    }
  };

  const handlePhotoUpload = async (file: File) => {
    try {
      setUploadingPhoto(true);

      const fileFormData = new FormData();
      fileFormData.append('file', file);

      const response = await fetch(`${API_URL}/users/${currentUserId}/profile/photo`, {
        method: 'POST',
        headers: {
          'authorization': 'Bearer ' + keycloak?.token
        },
        body: fileFormData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      const photoUrl = result.photo_url || result.url;

      if (photoUrl) {
        setFormData(prev => ({ ...prev, photo_url: photoUrl }));
        setHasUnsavedChanges(true);
        toast.success('Photo uploaded successfully. Save profile to confirm changes.');
      } else {
        throw new Error('No photo URL returned from server');
      }
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDocumentView = async (documentUrl?: string) => {
    if (!documentUrl || !keycloak?.token) {
      toast.error('Document not available');
      return;
    }

    // Determine document type key for loading state
    const docKey = documentConfigs.find(c => c.documentUrl === documentUrl)?.type || documentUrl;
    setViewingDocuments(prev => ({ ...prev, [docKey]: true }));

    // Open a new tab immediately so the user sees instant feedback
    const newTab = window.open('about:blank', '_blank');
    if (newTab) {
      newTab.document.write(`
        <html><head><title>Loading document...</title></head>
        <body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui,sans-serif;background:#f9fafb;">
          <div style="text-align:center;">
            <div style="width:40px;height:40px;border:3px solid #eaeaea;border-top-color:#171717;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 16px;"></div>
            <p style="color:#888;font-size:14px;">Loading document...</p>
            <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
          </div>
        </body></html>
      `);
    }

    try {
      const response = await fetch(documentUrl, {
        method: 'GET',
        headers: {
          'authorization': 'Bearer ' + keycloak.token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const openableTypes = ['pdf', 'image', 'text', 'video', 'audio'];
      const contentType = response.headers.get('content-type') || '';
      const canOpen = openableTypes.some(type =>
        contentType.toLowerCase().includes(type)
      );

      if (canOpen) {
        if (newTab) {
          newTab.location.href = url;
        } else {
          window.open(url, '_blank');
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        if (newTab) newTab.close();
        const a = document.createElement('a');
        a.href = url;
        a.download = 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch {
      if (newTab) newTab.close();
      toast.error('Failed to view document');
    } finally {
      setViewingDocuments(prev => ({ ...prev, [docKey]: false }));
    }
  };

  // Document configurations - DRY approach
  const documentConfigs: DocumentConfig[] = useMemo(() => [
    {
      type: 'NDA',
      label: 'NDA Document',
      accept: '.pdf,.doc,.docx',
      inputId: 'nda-upload',
      documentUrl: profile?.nda_document,
    },
    {
      type: 'CONTRACT',
      label: 'Contract Document',
      accept: '.pdf,.doc,.docx',
      inputId: 'contract-upload',
      documentUrl: profile?.contract_document,
    },
    {
      type: 'DRIVER_LICENSE',
      label: "Driver's License",
      accept: 'image/*,.pdf',
      inputId: 'license-upload',
      documentUrl: profile?.driver_license_document,
    },
  ], [profile?.nda_document, profile?.contract_document, profile?.driver_license_document]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-[#888] mb-4">Profile not found</p>
          <button
            onClick={loadUserProfile}
            className="px-4 py-2 bg-[#171717] text-white rounded hover:bg-[#171717]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Profile Completeness */}
      <ProfileCompleteness profile={profile} />

      {/* Header */}
      <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-[#eaeaea] p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Avatar
                firstName={profile.first_name}
                lastName={profile.last_name}
                email={profile.email}
                size={80}
                src={formData.photo_url || profile.photo_url}
              />
              {isEditing && (
                <div className="absolute -bottom-2 -right-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePhotoUpload(file);
                    }}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="flex items-center justify-center w-8 h-8 bg-[#171717] text-white rounded-full cursor-pointer hover:bg-[#171717] shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
                  >
                    {uploadingPhoto ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </label>
                </div>
              )}
            </div>
            <div>
              <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-[#171717]">
                {profile.first_name} {profile.last_name}
              </h1>
              <p className="text-[#555]">{profile.email}</p>
              {profile.position && (
                <p className="text-[#555]">{profile.position}</p>
              )}
              <p className="text-[#888]">{profile.city}, {profile.region}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#171717]"
              >
                Edit Profile
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-[#eaeaea] text-[#444] rounded-md hover:bg-[#fafafa]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                  className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-[#eaeaea] p-6">
          <h2 className="text-lg font-semibold mb-4">Personal Information</h2>

          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                      fieldErrors.first_name ? 'border-red-500' : 'border-[#eaeaea]'
                    }`}
                  />
                  {fieldErrors.first_name && (
                    <p className="mt-1 text-sm text-red-500">{fieldErrors.first_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                      fieldErrors.last_name ? 'border-red-500' : 'border-[#eaeaea]'
                    }`}
                  />
                  {fieldErrors.last_name && (
                    <p className="mt-1 text-sm text-red-500">{fieldErrors.last_name}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                    fieldErrors.email ? 'border-red-500' : 'border-[#eaeaea]'
                  }`}
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-sm text-red-500">{fieldErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (650) 441-5356"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                    phoneError || fieldErrors.phone ? 'border-red-500' : 'border-[#eaeaea]'
                  }`}
                />
                {(phoneError || fieldErrors.phone) && (
                  <p className="mt-1 text-sm text-red-500">{phoneError || fieldErrors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Position
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  placeholder="e.g., Senior Frontend Developer"
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-[#888]">Name:</span>
                <p className="text-[#171717]">{profile.first_name} {profile.last_name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-[#888]">Email:</span>
                <p className="text-[#171717]">{profile.email}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-[#888]">Phone:</span>
                <p className="text-[#171717]">{formatPhoneForDisplay(profile.phone) || 'Not provided'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-[#888]">Position:</span>
                <p className="text-[#171717]">{profile.position || 'Not specified'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Location & Work Range */}
        <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-[#eaeaea] p-6">
          <h2 className="text-lg font-semibold mb-4">Location & Work Range</h2>

          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">
                    Region/State
                  </label>
                  <select
                    name="region"
                    value={formData.region}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                  >
                    <option value="">Select Region</option>
                    {US_REGIONS.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                  />
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-[#444] mb-1">
                  Address
                </label>
                <div className="relative">
                  <input
                    ref={addressInputRef}
                    type="text"
                    value={addressQuery}
                    onChange={(e) => setAddressQuery(e.target.value)}
                    placeholder="Start typing an address..."
                    className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                  />
                  {isLoadingAddress && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#888]"></div>
                    </div>
                  )}
                  {formData.google_address_data && (
                    <button
                      type="button"
                      onClick={clearAddress}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#aaa] hover:text-[#555]"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Address Suggestions */}
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <div
                    ref={addressSuggestionsRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-[#eaeaea] rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] max-h-60 overflow-y-auto"
                  >
                    {addressSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.place_id}
                        type="button"
                        onClick={() => handleAddressSelect(suggestion)}
                        className="w-full px-4 py-2 text-left hover:bg-[#fafafa] focus:bg-[#f5f5f5] focus:outline-none"
                      >
                        <div className="font-medium text-[#171717]">
                          {suggestion.structured_formatting.main_text}
                        </div>
                        <div className="text-sm text-[#888]">
                          {suggestion.structured_formatting.secondary_text}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">
                    Hourly Rate ($)
                  </label>
                  <input
                    type="number"
                    name="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">
                    Work Range (miles)
                  </label>
                  <input
                    type="number"
                    name="range_miles"
                    value={formData.range_miles}
                    onChange={handleInputChange}
                    min="1"
                    max="500"
                    className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-[#888]">Address:</span>
                <p className="text-[#171717]">{profile.google_address_data?.formatted_address || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-[#888]">Location:</span>
                <p className="text-[#171717]">{profile.city}, {profile.region}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-[#888]">Hourly Rate:</span>
                <p className="text-[#171717]">${profile.rate}/hour</p>
              </div>
              <div>
                <span className="text-sm font-medium text-[#888]">Work Range:</span>
                <p className="text-[#171717]">{profile.range_miles} miles</p>
              </div>
            </div>
          )}
        </div>

        {/* Skills */}
        <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-[#eaeaea] p-6">
          <h2 className="text-lg font-semibold mb-4">Skills & Expertise</h2>

          {isEditing ? (
            <SkillsInput
              value={formData.skills}
              onChange={(skillsArray) => {
                setFormData(prev => ({ ...prev, skills: skillsArray }));
                setHasUnsavedChanges(true);
              }}
              placeholder="Type to search and add skills..."
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              {profile.skills && profile.skills.length > 0 ? (
                profile.skills.map((skill, index) => (
                  <span
                    key={`${skill}-${index}`}
                    className="px-3 py-1 bg-[#f5f5f5] text-[#555] rounded-full text-sm"
                  >
                    {skill}
                  </span>
                ))
              ) : (
                <p className="text-[#888]">No skills added yet</p>
              )}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-[#eaeaea] p-6">
          <h2 className="text-lg font-semibold mb-4">Documents</h2>

          <div className="space-y-4">
            {documentConfigs.map(config => (
              <DocumentRow
                key={config.type}
                config={config}
                isUploading={!!uploadingDocuments[config.type]}
                isViewing={!!viewingDocuments[config.type]}
                onView={handleDocumentView}
                onUpload={handleDocumentUpload}
                onDelete={handleDocumentDelete}
              />
            ))}
          </div>
        </div>

        {/* Projects */}
        <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-[#eaeaea] p-6">
          <h2 className="text-lg font-semibold mb-4">
            Projects
            {userProjects.length > 0 && (
              <span className="ml-2 text-sm font-normal text-[#888]">({userProjects.length})</span>
            )}
          </h2>

          {userProjects.length > 0 ? (
            <div className="space-y-2">
              {userProjects.map((project, index) => (
                <div
                  key={project.id || index}
                  className="p-3 border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      {project.id ? (
                        <a
                          href={`/projects/${project.id}`}
                          className="font-medium text-[#171717] hover:text-[#333] hover:underline"
                        >
                          {project.name || `Project ${index + 1}`}
                        </a>
                      ) : (
                        <p className="font-medium">{project.name || `Project ${index + 1}`}</p>
                      )}
                      {project.description && (
                        <p className="text-sm text-[#888] mt-1 truncate">{project.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      {project.status && (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          project.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                          project.status === 'IN_PROGRESS' ? 'bg-[#f5f5f5] text-[#171717]' :
                          'bg-[#f5f5f5] text-[#555]'
                        }`}>
                          {project.status.replace('_', ' ')}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        project.role === 'Manager' ? 'bg-purple-100 text-purple-700' : 'bg-[#f5f5f5] text-[#555]'
                      }`}>
                        {project.role}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-[#ccc] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-[#888]">No projects assigned yet</p>
              <p className="text-sm text-[#aaa] mt-1">Projects you're assigned to will appear here</p>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-[#eaeaea] p-6">
          <h2 className="text-lg font-semibold mb-4">Quick Stats</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-[#f5f5f5] rounded-lg">
              <div className="text-[20px] font-semibold tracking-[-0.01em] text-[#171717]">
                {profile.skills?.length || 0}
              </div>
              <div className="text-xs text-[#888] mt-1">Skills</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-[20px] font-semibold tracking-[-0.01em] text-green-600">
                {userProjects.length}
              </div>
              <div className="text-xs text-[#888] mt-1">Projects</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-[20px] font-semibold tracking-[-0.01em] text-purple-600">
                {[profile.nda_document, profile.contract_document, profile.driver_license_document].filter(Boolean).length}
              </div>
              <div className="text-xs text-[#888] mt-1">Documents</div>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <div className="text-[20px] font-semibold tracking-[-0.01em] text-orange-600">
                {profile.range_miles}
              </div>
              <div className="text-xs text-[#888] mt-1">Miles Range</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
